import {
  FileText,
  FolderOpen,
  Upload,
  Clock,
  CheckCircle2,
  AlertTriangle,
  File,
} from 'lucide-react';
import React from 'react';
import { useGetDocumentSummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const DocumentsWidget: React.FC<WidgetProps> = ({ scope, projectId, size: _size }) => {
  const { data, isLoading, error } = useGetDocumentSummaryQuery(
    { projectId: projectId! },
    { skip: scope !== 'project' || !projectId, pollingInterval: 300000 }
  );

  if (scope !== 'project') {
    return (
      <WidgetEmptyState
        title="Project Required"
        description="Select a project to view document summary"
        icon={FolderOpen}
      />
    );
  }

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load documents</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <WidgetEmptyState
        title="No Documents"
        description="No documents have been uploaded yet"
        icon={FolderOpen}
      />
    );
  }

  const isCompact = _size === '1x1';

  return (
    <div className="space-y-4">
      {/* Document Count Overview */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-600">Total</span>
          </div>
          <p className="text-xl font-bold text-blue-700">{data.totalDocuments}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Upload className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Recent</span>
          </div>
          <p className="text-xl font-bold text-green-700">{data.recentUploads}</p>
        </div>
      </div>

      {/* Review Status */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-yellow-50 rounded-lg p-2 text-center">
          <Clock className="h-4 w-4 text-yellow-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Pending</p>
          <p className="text-sm font-bold text-yellow-700">{data.pendingReview}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-2 text-center">
          <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Approved</p>
          <p className="text-sm font-bold text-green-700">{data.approved}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-2 text-center">
          <AlertTriangle className="h-4 w-4 text-red-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Rejected</p>
          <p className="text-sm font-bold text-red-700">{data.rejected}</p>
        </div>
      </div>

      {/* Document Types (for larger sizes) */}
      {!isCompact && data.byType && data.byType.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">By Type</p>
          <div className="space-y-2">
            {data.byType.slice(0, _size === '2x2' ? 5 : 3).map((type, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <File className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-600 truncate max-w-[100px]">{type.type}</span>
                </div>
                <span className="text-xs font-medium text-gray-900">{type.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats Summary */}
      {_size === '2x2' && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Review Rate</span>
            <span className="font-medium text-gray-900">
              {data.totalDocuments > 0
                ? Math.round(((data.approved + data.rejected) / data.totalDocuments) * 100)
                : 0}
              %
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsWidget;
